import { MessageFactory, unwrapMessage, ByteStreamReader, ModelMessage, CreateSubModelMessage, PeersSelectionVisibilityMessage } from "flavr-common";
import { UserInfoMessage as CoreUserInfoMessage } from "@messages/session/PeerMessages";
import { HistoryEventType } from "@messages/scripting/ScriptMessages";
import { AttachToModelMessage } from "@messages/attachment/AttachmentMessage";
import { getOrCreateModelFromAbsolutePath, getModelFromAbsolutePath, modelPool } from "@modules/model";
import { ModelPath } from "@modules/model/modelPath/ModelPath";
import { MessageService } from "../../../ui/services/message.service";
import { BufferMessageType } from "../MultiplayerTypes";
import { usersService } from "../UsersService";
import { NetworkObjectFactory } from "./NetworkObjectFactory";
import { FragmentFlag } from "./PeerMessageTypes";
import { historyEventService } from "./HistoryEventService";
export class PeerMessageHandler {
    constructor(mgr) {
        this.mgr = mgr;
        this.busMessageFragments = [];
        this.pendingBusMessages = [];
    }
    canMessageBeAccepted(msg) {
        if (!ModelMessage.isSubject(msg))
            return true;
        if (CreateSubModelMessage.is(msg)) {
            const parentPath = new ModelPath(msg.path).getParent();
            if (parentPath === undefined)
                return false;
            const parent = getModelFromAbsolutePath(parentPath.toString());
            if (parent === undefined)
                return false;
            return true;
        }
        if (AttachToModelMessage.is(msg)) {
            return modelPool.hasModel(msg.childUID) && modelPool.hasModel(msg.parentUID);
        }
        return modelPool.hasModel(msg.uid);
    }
    onPeerData(clientId, msg) {
        switch (msg.channel) {
            case "bulk":
                for (const m of msg.payload) {
                    this.onPeerData(clientId, m);
                }
                break;
            case "userinfo":
                this.handleUserInfo(clientId, msg);
                break;
            case "bus":
                const parsedMsg = unwrapMessage(msg.payload.msg);
                this.handlePeerBusMessage(clientId, parsedMsg);
                break;
        }
    }
    onPeerBuffer(clientId, buffer) {
        const stream = new ByteStreamReader(buffer);
        const msgType = stream.getUint8();
        switch (msgType) {
            case BufferMessageType.TymeSync:
                {
                    const client = this.mgr.peers.get(clientId);
                    if (client) {
                        client.handleTimeSync(stream.getFloat64());
                    }
                }
                break;
            case BufferMessageType.TimeSyncAnswer:
                {
                    const client = this.mgr.peers.get(clientId);
                    if (client) {
                        client.handleTimeSyncAnswer(stream.getFloat64(), stream.getFloat64());
                    }
                }
                break;
            case BufferMessageType.CreateObject:
                this.handleCreateObject(clientId, stream);
                break;
            case BufferMessageType.RemoveObject:
                this.handleRemoveObject(stream);
                break;
            case BufferMessageType.SyncData:
                this.handleSyncDataMessage(stream);
                break;
            case BufferMessageType.RPC:
                this.handleRPC(stream);
                break;
            case BufferMessageType.RoomChanged:
                this.handleRoomChanged(clientId, stream.getUtf16String());
                break;
            case BufferMessageType.BusMsg: {
                this.handleBusMessages(stream, clientId);
                break;
            }
        }
    }
    handleUserInfo(clientId, msg) {
        const existingUser = usersService.getUserByClientId(clientId);
        const wasLoading = (existingUser === null || existingUser === void 0 ? void 0 : existingUser.isLoading) === true;
        const oldDisplayName = usersService.getDisplayName(clientId);
        const peer = this.mgr.getPeer(clientId);
        if (!peer) {
            console.error(`[multiplayer] Got user info from unknown peer ${clientId}`);
            return;
        }
        const isNewUser = usersService.addUser({
            clientId,
            clientIdx: peer.remoteClientIdx,
            identity: msg.payload.identity,
            avatar: msg.payload.avatar,
            customUsername: msg.payload.customUsername,
            guest: msg.payload.guest,
            isLoading: false
        });
        if (isNewUser || wasLoading) {
            // Peer is a new user
            historyEventService.addLocalHistoryEvent({
                type: HistoryEventType.userJoined,
                value: usersService.getDisplayName(clientId),
                clientId: clientId
            });
        }
        else {
            // Peer is a known user
            const avatarObj = this.mgr.peerAvatars.get(clientId);
            const user = usersService.getUserByClientId(clientId);
            if (avatarObj && user) {
                avatarObj.updateVisualisation(user);
            }
            if (!wasLoading && oldDisplayName !== usersService.getDisplayName(clientId)) {
                const msg = `User "${oldDisplayName}" ändert seinen Namen zu "${usersService.getDisplayName(clientId)}"`;
                MessageService.showMessage("info", msg);
                // The new UI does not allow changing the username, so we should not get here
                throw new Error("User already exists");
            }
        }
        this.mgr.connection.send(new CoreUserInfoMessage(clientId, peer.remoteClientIdx, msg.payload.avatar, msg.payload.identity, msg.payload.customUsername));
    }
    handleCreateObject(clientId, stream) {
        const msgCount = stream.getUint32();
        for (let i = 0; i < msgCount; ++i) {
            const netUid = stream.uid(0);
            const objectType = stream.getUint16();
            const netObject = NetworkObjectFactory.create(this.mgr, objectType, clientId, netUid);
            if (netObject === undefined)
                return;
            if (netObject.constructorSerialization !== undefined) {
                netObject.constructorSerialization(stream);
            }
            this.mgr.registerNetworkObject(netUid, netObject);
            if (netObject.onRemoteCreate !== undefined) {
                netObject.onRemoteCreate();
            }
        }
    }
    handleRemoveObject(stream) {
        const msgCount = stream.getUint32();
        for (let i = 0; i < msgCount; ++i) {
            const netUid = stream.uid(0);
            this.mgr.removeRemoteObject(netUid);
        }
    }
    handleSyncDataMessage(stream) {
        const msgCount = stream.getUint32();
        for (let i = 0; i < msgCount; ++i) {
            const netUid = stream.getInt32();
            const obj = this.mgr.getSyncObjectByUid(netUid);
            if (!obj || !obj.serialization)
                break;
            obj.serialization(stream);
        }
    }
    handleRPC(stream) {
        const count = stream.getUint32();
        for (let i = 0; i < count; ++i) {
            const netUid = stream.uid(0);
            const cmdId = stream.getUint16();
            const paramCount = stream.getUint8();
            let paramArray;
            if (paramCount) {
                paramArray = new Array(paramCount);
                for (let pIdx = 0; pIdx < paramCount; ++pIdx) {
                    const value = undefined;
                    paramArray[pIdx] = stream.type(value);
                }
            }
            this.mgr.onRPC(netUid, cmdId, paramArray);
        }
    }
    mergeFragments() {
        let byteLength = 0;
        for (const s of this.busMessageFragments) {
            byteLength += s.getUnreadBytes();
        }
        const buffer = new Uint8Array(byteLength);
        let offset = 0;
        for (const s of this.busMessageFragments) {
            buffer.set(new Uint8Array(s.getUnreadArrayBuffer()), offset);
            offset += s.getUnreadBytes();
        }
        this.busMessageFragments = [];
        return new ByteStreamReader(buffer.buffer);
    }
    handleBusMessages(stream, clientId) {
        const fragmentFlag = stream.getUint8();
        let msgCount = 0;
        if (fragmentFlag === FragmentFlag.DontFragment) {
            msgCount = stream.getUint32();
            if (this.busMessageFragments.length > 0) {
                this.busMessageFragments = [];
                console.warn("[multiplayer] unhandled fragments");
            }
        }
        else {
            this.busMessageFragments.push(stream);
            if (fragmentFlag === FragmentFlag.MoreFragments) {
                return;
            }
            else {
                stream = this.mergeFragments();
                msgCount = stream.getUint32();
            }
        }
        const messages = [];
        for (let i = 0; i < msgCount; ++i) {
            const typeHash = stream.getTypeHash();
            const msg = MessageFactory.create(typeHash);
            if (!msg.serialization) {
                console.error("[multiplayer] serialization is not defined", msg.type);
                return;
            }
            try {
                msg.serialization(stream);
                if (this.canMessageBeAccepted(msg)) {
                    messages.push(msg);
                }
                else {
                    this.enqueuePendingBusMessage(clientId, msg);
                }
                // TODO: this should be done differently
                if (PeersSelectionVisibilityMessage.is(msg)) {
                    this.mgr.changeRemoteSelectionVisibility(msg);
                }
            }
            catch (error) {
                console.error(`[multiplayer] Failed to deserialize ${msg.type}:`, error);
                return;
            }
        }
        this.dispatchAcceptedMessages(messages, clientId);
    }
    handleRoomChanged(clientId, roomPath) {
        const room = getOrCreateModelFromAbsolutePath(roomPath); // ToDo: Do we want NO_MESSAGING here?
        const peer = this.mgr.getPeer(clientId);
        if (peer !== undefined) {
            peer.onRoomChanged(room.getUID());
        }
        const avatar = this.mgr.peerAvatars.get(clientId);
        if (avatar !== undefined) {
            avatar.onRemoteRoomChanged(room.getUID());
        }
        for (const controller of this.mgr.peersControllers.values()) {
            if (controller.ownerId === clientId) {
                controller.onRemoteRoomChanged(room.getUID());
            }
        }
        this.drainPendingBusMessages();
    }
    handlePeerBusMessage(clientId, msg) {
        if (this.canMessageBeAccepted(msg)) {
            this.dispatchAcceptedMessages([msg], clientId);
            return;
        }
        this.enqueuePendingBusMessage(clientId, msg);
    }
    enqueuePendingBusMessage(clientId, msg) {
        this.pendingBusMessages.push({ clientId, msg });
    }
    dispatchAcceptedMessages(messages, clientId) {
        if (messages.length === 0)
            return;
        this.mgr.connection.sendMultiple(messages, undefined, undefined, clientId, undefined, "peer");
        this.drainPendingBusMessages();
    }
    drainPendingBusMessages() {
        if (this.pendingBusMessages.length === 0)
            return;
        let didProcessMessage = false;
        do {
            didProcessMessage = false;
            const stillPending = [];
            for (const pending of this.pendingBusMessages) {
                if (this.canMessageBeAccepted(pending.msg)) {
                    this.mgr.connection.sendMultiple([pending.msg], undefined, undefined, pending.clientId, undefined, "peer");
                    didProcessMessage = true;
                }
                else {
                    stillPending.push(pending);
                }
            }
            this.pendingBusMessages = stillPending;
        } while (didProcessMessage && this.pendingBusMessages.length > 0);
    }
}
